import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["area", "fileInput", "hiddenField", "preview"]
  static values = {
    presignUrl: String,
    maxFiles: { type: Number, default: 3 },
    accept: { type: String, default: "image/*,video/*" },
  }

  connect() {
    console.log(`[dropzone] connect: maxFiles=${this.maxFilesValue} presignUrl=${this.presignUrlValue}`)
    this.uploadedUrls = []
    try {
      const existing = JSON.parse(this.hiddenFieldTarget.value)
      if (Array.isArray(existing)) this.uploadedUrls = existing
    } catch (e) {}
    this.renderPreviews()
  }

  openPicker(e) {
    e.preventDefault()
    this.fileInputTarget.click()
  }

  dragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    this.areaTarget.classList.add("border-accent", "bg-accent/10")
    this.areaTarget.classList.remove("border-gray-600")
  }

  dragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    this.areaTarget.classList.remove("border-accent", "bg-accent/10")
    this.areaTarget.classList.add("border-gray-600")
  }

  drop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.areaTarget.classList.remove("border-accent", "bg-accent/10")
    this.areaTarget.classList.add("border-gray-600")

    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    )
    if (files.length === 0) return
    this.uploadFiles(files)
  }

  filesSelected(e) {
    const files = Array.from(e.target.files).filter(f =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    )
    if (files.length === 0) return
    this.uploadFiles(files)
    e.target.value = ""
  }

  async uploadFiles(files) {
    const remaining = this.maxFilesValue - this.uploadedUrls.length
    if (remaining <= 0) {
      console.warn(`[dropzone] max files (${this.maxFilesValue}) reached, skipping uploads`)
      this.showToast(`Max ${this.maxFilesValue} media files allowed`, "warning")
      return
    }
    const batch = files.slice(0, remaining)
    if (batch.length < files.length) {
      this.showToast(`Max ${this.maxFilesValue} media files allowed — ${files.length - batch.length} skipped`, "warning")
    }
    for (const file of batch) {
      await this.uploadSingleFile(file)
    }
  }

  async uploadSingleFile(file) {
    const index = this.uploadedUrls.length
    const previewId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    this.addUploadingPreview(previewId, file)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      console.log(`[dropzone] fetching presigned URL for: ${file.name} (${file.type})`)

      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ filename: file.name }),
      })

      if (!presignRes.ok) {
        throw new Error(`Presign failed: ${presignRes.status}`)
      }

      const { upload_url, public_url, s3_key } = await presignRes.json()
      console.log(`[dropzone] presigned OK: ${s3_key}`)

      await this.uploadToS3(upload_url, file, previewId)

      this.uploadedUrls.push(public_url)
      this.hiddenFieldTarget.value = JSON.stringify(this.uploadedUrls)
      console.log(`[dropzone] upload complete: ${public_url} (${this.uploadedUrls.length}/${this.maxFilesValue})`)

      this.markUploadComplete(previewId, public_url)
    } catch (err) {
      console.error(`[dropzone] upload failed:`, err)
      this.markUploadFailed(previewId, err.message)
    }
  }

  uploadToS3(uploadUrl, file, previewId) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open("PUT", uploadUrl)
      xhr.setRequestHeader("Content-Type", file.type)

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100)
          this.updateProgress(previewId, pct)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status}`))
        }
      })

      xhr.addEventListener("error", () => reject(new Error("Network error during upload")))
      xhr.send(file)
    })
  }

  addUploadingPreview(id, file) {
    const isVideo = file.type.startsWith("video/")
    const inner = isVideo
      ? `<div class="w-full h-full bg-gray-800 flex items-center justify-center">
           <svg class="w-8 h-8 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
             <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9A2.25 2.25 0 004.5 18.75z" />
           </svg>
         </div>`
      : `<img src="" class="w-full h-full object-cover opacity-50" data-thumb />`

    const div = document.createElement("div")
    div.id = id
    div.className = "relative inline-block mr-2 mb-2"
    div.innerHTML = `
      <div class="w-24 h-24 rounded-lg overflow-hidden border-2 border-accent relative">
        ${inner}
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="w-16 bg-gray-700 rounded-full h-2">
            <div class="bg-accent h-2 rounded-full transition-all" style="width: 0%" data-progress></div>
          </div>
        </div>
      </div>
      <span class="text-xs text-gray-400 block text-center mt-1 truncate w-24">${file.name}</span>
    `
    this.previewTarget.appendChild(div)

    if (!isVideo) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const thumb = div.querySelector("[data-thumb]")
        if (thumb) thumb.src = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  updateProgress(id, pct) {
    const el = document.getElementById(id)
    if (!el) return
    const bar = el.querySelector("[data-progress]")
    if (bar) bar.style.width = `${pct}%`
  }

  markUploadComplete(id, url) {
    const el = document.getElementById(id)
    if (!el) return
    const img = el.querySelector("img")
    if (img) img.classList.remove("opacity-50")
    const overlay = el.querySelector(".absolute.inset-0")
    if (overlay) {
      overlay.innerHTML = `
        <div class="absolute top-1 right-1 bg-green rounded-full p-0.5">
          <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      `
    }
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute top-1 left-1 bg-brand-red rounded-full p-0.5 hover:bg-red-700 transition"
    removeBtn.innerHTML = `<svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`
    removeBtn.addEventListener("click", () => this.removeUpload(url, id))
    el.querySelector(".relative.inline-block")?.appendChild(removeBtn) || el.appendChild(removeBtn)
  }

  markUploadFailed(id, message) {
    const el = document.getElementById(id)
    if (!el) return
    el.querySelector(".border-2")?.classList.replace("border-accent", "border-brand-red")
    const overlay = el.querySelector(".absolute.inset-0")
    if (overlay) {
      overlay.innerHTML = `
        <div class="absolute inset-0 flex items-center justify-center bg-brand-red/50 rounded-lg">
          <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
      `
    }
  }

  removeUpload(url, elementId) {
    this.uploadedUrls = this.uploadedUrls.filter(u => u !== url)
    this.hiddenFieldTarget.value = JSON.stringify(this.uploadedUrls)
    const el = document.getElementById(elementId)
    if (el) el.remove()
    console.log(`[dropzone] removed upload: ${url} (${this.uploadedUrls.length}/${this.maxFilesValue})`)
  }

  renderPreviews() {
    if (this.uploadedUrls.length === 0) return
    this.previewTarget.innerHTML = ""
    this.uploadedUrls.forEach((url, i) => {
      const id = `existing-${i}`
      const div = document.createElement("div")
      div.id = id
      div.className = "relative inline-block mr-2 mb-2"
      div.innerHTML = `
        <div class="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-600 relative">
          <img src="${url}" class="w-full h-full object-cover" onerror="this.closest('.w-24').querySelector('img')?.remove(); this.closest('.w-24').innerHTML='<div class=\\'w-full h-full bg-gray-800 flex items-center justify-center\\'><svg class=\\'w-8 h-8 text-white/60\\' fill=\\'none\\' viewBox=\\'0 0 24 24\\' stroke=\\'currentColor\\' stroke-width=\\'1.5\\'><path stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' d=\\'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9A2.25 2.25 0 004.5 18.75z' /></svg></div>'" />
        </div>
      `
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "absolute top-1 left-1 bg-brand-red rounded-full p-0.5 hover:bg-red-700 transition"
      removeBtn.innerHTML = `<svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`
      removeBtn.addEventListener("click", () => this.removeUpload(url, id))
      div.appendChild(removeBtn)
      this.previewTarget.appendChild(div)
    })
  }

  showToast(msg, type) {
    const existing = document.querySelector("[data-dropzone-toast]")
    if (existing) existing.remove()
    const toast = document.createElement("div")
    toast.setAttribute("data-dropzone-toast", "")
    toast.className = `fixed bottom-4 right-4 z-50 px-4 py-2 rounded-lg text-sm shadow-lg text-white ${
      type === "warning" ? "bg-gold text-gray-900" : "bg-brand-red"
    }`
    toast.textContent = msg
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }
}
